import type {
  NewsAlertScheduleFrequency,
  NewsAlertSortOrder,
} from "@/data-access/news-alerts/queries/news-alerts-queries";
import type { AutocompleteOption } from "../ui/molecules/autocomplete/Autocomplete.type";
import type { ScheduleFrequencyOption } from "./NewsAlerts.types";

export const isValidEmail = (email: string): boolean => {
  if (typeof email !== "string") return false;
  const trimmed = email.trim();

  // Improved pattern:
  // - No leading/trailing dot in local part
  // - At least one character before and after "@"
  // - Domain must contain a dot and valid chars
  const emailRegex = /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/;

  if (!emailRegex.test(trimmed)) return false;

  // Additional logical checks
  const [localPart] = trimmed.split("@");
  if (!localPart) return false;

  if (localPart.startsWith(".") || localPart.endsWith(".")) return false;

  return true;
};

/**
 * Time frequency options are 30 min intervals AM & PM
 */
export const generateTimeOptions = (): ScheduleFrequencyOption[] => {
  const options: ScheduleFrequencyOption[] = [];
  const startHour = 0; // Start at 12:00 AM

  for (let i = 0; i < 48; i++) {
    const totalMinutes = startHour * 60 + i * 30;
    const hours = Math.floor(totalMinutes / 60) % 24;
    const minutes = totalMinutes % 60;

    const hour12 = hours === 0 ? 12 : hours > 12 ? hours - 12 : hours;
    const period = hours < 12 ? "AM" : "PM";
    const formattedTime = `${hour12}:${minutes.toString().padStart(2, "0")} ${period}`;

    options.push({
      label: formattedTime,
      value: formattedTime,
    });
  }

  return options;
};

export const ARTICLE_LIMIT_OPTIONS = [
  { value: 10, label: "10" },
  { value: 15, label: "15" },
  { value: 20, label: "20" },
  { value: 25, label: "25" },
];

export const SORT_ORDER_LABELS = {
  DATE_DESC: "Published date (Newest → Oldest)",
  DATE_ASC: "Published date (Oldest → Newest)",
  RELEVANCE: "Relevance",
  TITLE_ASC: "Headline (A → Z)",
  TITLE_DESC: "Headline (Z → A)",
} as const satisfies Readonly<Record<NewsAlertSortOrder, string>>;

export const SORT_ORDER_OPTIONS: AutocompleteOption[] = (
  Object.entries(SORT_ORDER_LABELS) as Array<[NewsAlertSortOrder, string]>
).map(([value, label]) => ({ value, label }));

export const SCHEDULE_TIME_OPTIONS = generateTimeOptions();

export const SCHEDULE_FREQUENCY_LABELS = {
  DAILY: "Daily",
  WEEKDAYS: "Weekdays",
  MONTHLY: "Monthly",
} as const satisfies Readonly<Record<NewsAlertScheduleFrequency, string>>;
